package fr.frux.feature.treedetail.move

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material.icons.outlined.Warning
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.dp
import fr.frux.core.common.error.AppError
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.error.userMessage
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_move_add_here
import fr.frux.feature.treedetail.resources.treedetail_move_aim_hint
import fr.frux.feature.treedetail.resources.treedetail_move_aim_title
import fr.frux.feature.treedetail.resources.treedetail_move_legend
import fr.frux.feature.treedetail.resources.treedetail_move_recenter
import fr.frux.feature.treedetail.resources.treedetail_move_report_gone
import fr.frux.feature.treedetail.resources.treedetail_move_save
import fr.frux.feature.treedetail.resources.treedetail_move_too_far_body
import fr.frux.feature.treedetail.resources.treedetail_move_too_far_title
import fr.frux.shared.content.TreeCorrectionLimits
import org.jetbrains.compose.resources.stringResource
import kotlin.math.ceil
import kotlin.math.roundToInt

private val MAX_MOVE_LABEL = TreeCorrectionLimits.MAX_MOVE_METERS.roundToInt()

/** Ce que la feuille du déplacement affiche, lu depuis la visée et le ViewModel. */
internal data class MovePanelState(
    val canReportGone: Boolean,
    val distanceMeters: Double?,
    val saving: Boolean,
    val reporting: Boolean,
    val error: AppError?,
) {
    val tooFar: Boolean get() = distanceMeters != null && distanceMeters > TreeCorrectionLimits.MAX_MOVE_METERS

    /** Sous le bruit de carte, rien ne changerait : le serveur n'écrirait aucune révision. */
    val canSave: Boolean get() = distanceMeters != null && distanceMeters >= TreeCorrectionLimits.POSITION_NOISE_METERS
}

internal class MovePanelActions(
    val onSave: () -> Unit,
    val onAddHere: () -> Unit,
    val onReportGone: () -> Unit,
    val onRecenter: () -> Unit,
)

/** Contenu de la feuille basse (téléphone) ou de la colonne (grand écran). */
@Composable
internal fun MovePanel(
    panel: MovePanelState,
    actions: MovePanelActions,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(14.dp)) {
        if (panel.tooFar) {
            TooFarContent(panel, actions)
        } else {
            InCircleContent(panel, actions)
        }
    }
}

@Composable
private fun InCircleContent(
    panel: MovePanelState,
    actions: MovePanelActions,
) {
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(stringResource(Res.string.treedetail_move_aim_title), style = MaterialTheme.typography.headlineSmall)
        Text(
            text = stringResource(Res.string.treedetail_move_aim_hint, MAX_MOVE_LABEL),
            style = MaterialTheme.typography.bodyLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        DashedDot()
        Text(
            text = stringResource(Res.string.treedetail_move_legend, MAX_MOVE_LABEL),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
    ErrorLine(panel.error)
    FruxButton(
        text = stringResource(Res.string.treedetail_move_save),
        onClick = actions.onSave,
        modifier = Modifier.fillMaxWidth(),
        enabled = panel.canSave,
        loading = panel.saving,
    )
}

@Composable
private fun TooFarContent(
    panel: MovePanelState,
    actions: MovePanelActions,
) {
    val scheme = MaterialTheme.colorScheme
    Row(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.Top) {
        Box(
            modifier = Modifier.size(40.dp).background(scheme.errorContainer, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                imageVector = Icons.Outlined.Warning,
                contentDescription = null,
                tint = scheme.onErrorContainer,
                modifier = Modifier.size(20.dp),
            )
        }
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(
                text = stringResource(Res.string.treedetail_move_too_far_title),
                style = MaterialTheme.typography.headlineSmall,
            )
            Text(
                text =
                    stringResource(
                        Res.string.treedetail_move_too_far_body,
                        ceil(panel.distanceMeters ?: 0.0).roundToInt(), // par excès : jamais « à 50 m » au-delà de 50 m
                        MAX_MOVE_LABEL,
                    ),
                style = MaterialTheme.typography.bodyLarge,
                color = scheme.onSurfaceVariant,
            )
        }
    }
    ErrorLine(panel.error)
    FruxButton(
        text = stringResource(Res.string.treedetail_move_add_here),
        onClick = actions.onAddHere,
        modifier = Modifier.fillMaxWidth(),
        icon = Icons.Outlined.Add,
    )
    if (panel.canReportGone) {
        FruxButton(
            text = stringResource(Res.string.treedetail_move_report_gone),
            onClick = actions.onReportGone,
            modifier = Modifier.fillMaxWidth(),
            style = FruxButtonStyle.Outline,
            loading = panel.reporting,
            icon = Icons.Outlined.Flag,
        )
    }
    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
        FruxButton(
            text = stringResource(Res.string.treedetail_move_recenter),
            onClick = actions.onRecenter,
            style = FruxButtonStyle.Text,
        )
    }
}

/** Rond en tirets de la légende, rappel du cercle dessiné sur la carte. */
@Composable
private fun DashedDot() {
    val color = MaterialTheme.colorScheme.primary
    Canvas(modifier = Modifier.size(14.dp)) {
        val stroke = 2.dp.toPx()
        drawCircle(
            color = color,
            radius = (size.minDimension - stroke) / 2,
            style = Stroke(width = stroke, pathEffect = PathEffect.dashPathEffect(floatArrayOf(3f, 3f))),
        )
    }
}

@Composable
private fun ErrorLine(error: AppError?) {
    if (error == null) return
    Text(
        text = error.userMessage(),
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.error,
    )
}
